import { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import './Suppliers.css';

const API = import.meta.env.VITE_API_URL;

async function getToken(instance) {
  try {
    const accounts = instance.getAllAccounts();
    if (!accounts.length) return null;
    const resp = await instance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return resp.idToken;
  } catch {
    const accounts = instance.getAllAccounts();
    return accounts[0]?.idToken || null;
  }
}

const DEFAULT_PAGE_SIZE = 50;

const COLUMNS = [
  { key: 'type_medicine_disease', label: 'Type Medicine / Disease', placeholder: 'Filter type...' },
  { key: 'werkzame_stof', label: 'Molecule / Active Ingredient', placeholder: 'Filter molecule...' },
  { key: 'supplier', label: 'Supplier', placeholder: 'Filter supplier...' },
  { key: 'contract', label: 'Contract', placeholder: 'Filter contract...' },
];

const EMPTY_FILTERS = Object.fromEntries(COLUMNS.map(c => [c.key, '']));

export default function SwitchList({ listType }) {
  const isRed = listType === 'red';
  const title = isRed ? 'Red List' : 'Orange List';
  const accentColor = isRed ? '#dc2626' : '#f59e0b';

  const { instance } = useMsal();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/api/market-intelligence/switch-list/${listType}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.rows || []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [instance, listType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilters = COLUMNS.some(c => filters[c.key]);

  const filtered = useMemo(() => {
    if (!hasFilters) return rows;
    return rows.filter(row =>
      COLUMNS.every(c => {
        const q = filters[c.key];
        if (!q) return true;
        return (row[c.key] || '').toLowerCase().includes(q.toLowerCase());
      })
    );
  }, [rows, filters, hasFilters]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [filters]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / DEFAULT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * DEFAULT_PAGE_SIZE;
  const pageEnd = Math.min(safePage * DEFAULT_PAGE_SIZE, filtered.length);
  const pageRows = filtered.slice(pageStart, pageEnd);

  const paginationNums = (() => {
    const win = 5;
    let s = Math.max(1, safePage - Math.floor(win / 2));
    let e = Math.min(pageCount, s + win - 1);
    s = Math.max(1, e - win + 1);
    const nums = [];
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  })();

  const inputStyle = { width: '100%', padding: '5px 10px', fontSize: 12.5, border: '1px solid #d0d7de', borderRadius: 6, background: 'var(--card)', color: 'var(--t1)', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px', background: 'var(--surf)' }}>
      <h4 style={{ fontWeight: 700, marginBottom: 4, color: 'var(--t1)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          display: 'inline-block', width: 14, height: 14, borderRadius: 3,
          background: accentColor, flexShrink: 0,
        }} />
        {title}
      </h4>
      <div style={{ marginBottom: 14, fontSize: 11.5, color: 'var(--t3)' }}>
        Molecules on the NHG {title.toLowerCase()} — switching is {isRed ? 'not recommended' : 'possible with caution'}.
      </div>

      {/* Filters */}
      {!loading && !error && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {COLUMNS.map(c => (
            <div key={c.key} style={{ minWidth: 140, flex: 1 }}>
              <div style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>{c.label}</div>
              <input
                style={inputStyle}
                placeholder={c.placeholder}
                value={filters[c.key]}
                onChange={e => setFilters(f => ({ ...f, [c.key]: e.target.value }))}
              />
            </div>
          ))}
          {hasFilters && (
            <button
              type="button"
              onClick={() => setFilters(EMPTY_FILTERS)}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, color: 'var(--teal)', background: 'var(--card)', border: '1px solid var(--teal)', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
            >Clear filters</button>
          )}
        </div>
      )}

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          Failed to load data: {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading...</div>
      )}

      {!loading && !error && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh' }}>
            <table className="sup-table" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  {COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} style={{ padding: 32, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                      {hasFilters ? 'No rows match the current filters' : 'No data available'}
                    </td>
                  </tr>
                ) : pageRows.map((row, i) => (
                  <tr key={i}>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--t3)' }}>{pageStart + i + 1}</td>
                    {COLUMNS.map(c => (
                      <td key={c.key} style={c.key === 'supplier' ? { whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 180, maxWidth: 360 } : { whiteSpace: 'nowrap' }}>{row[c.key] ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
            <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
              {paginationNums.map(n => (
                <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
              ))}
              <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
            </nav>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
              Showing {filtered.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {filtered.length}{hasFilters ? ` (filtered from ${rows.length})` : ''} rows
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
